import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getRuntimeConfig } from '../config/runtimeConfig';
import { fetchTicket, TicketError } from './ticketApi';

/**
 * Sesiunea aplicației, fără login.
 *
 * Ține un bilet de acces de scurtă durată, exclusiv în memorie — nu în
 * localStorage, unde l-ar putea citi orice script injectat — și îl reînnoiește
 * înainte să expire. Biletul e singurul lucru pe care îl vede browserul;
 * credențialele MQTT rămân în backend.
 */

export type SessionStatus = 'demo' | 'loading' | 'ready' | 'error';

interface SessionContextValue {
  status: SessionStatus;
  error: string | null;
  /**
   * Codul HTTP al ultimei încercări eșuate; `0` dacă serverul n-a răspuns deloc.
   *
   * Ecranul de pornire îl citește ca să nu confunde „stația e jos” cu „stația
   * e sus, dar te refuză”.
   */
  errorStatus: number | null;
  /** Token-ul curent pentru handshake-ul WebSocket și antetul REST. */
  getToken: () => string | null;
  /** Se schimbă la fiecare bilet nou — forțează reconectarea socket-ului. */
  tokenVersion: number;
  /** Cere imediat un bilet nou (la eroare sau la refuz din partea serverului). */
  renew: () => void;
}

const SessionContext = createContext<SessionContextValue | null>(null);

/** Reînnoim cu un minut înainte de expirare, ca să nu prindem fereastra moartă. */
const RENEW_MARGIN_SECONDS = 60;
const MIN_RENEW_DELAY_MS = 5_000;
/** După o eroare reîncercăm rar; între timp UI-ul arată motivul. */
const RETRY_DELAY_MS = 15_000;

export function SessionProvider({ children }: { children: ReactNode }) {
  const demo = getRuntimeConfig().demo;

  const [status, setStatus] = useState<SessionStatus>(demo ? 'demo' : 'loading');
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [tokenVersion, setTokenVersion] = useState(0);
  const [renewNonce, setRenewNonce] = useState(0);

  const tokenRef = useRef<string | null>(null);

  useEffect(() => {
    if (demo) return;

    const controller = new AbortController();
    let timer: number | undefined;
    let cancelled = false;

    const load = () => {
      fetchTicket(controller.signal)
        .then((ticket) => {
          if (cancelled) return;
          tokenRef.current = ticket.token;
          setStatus('ready');
          setError(null);
          setErrorStatus(null);
          setTokenVersion((version) => version + 1);

          if (ticket.expiresIn > 0) {
            const delay = Math.max(
              MIN_RENEW_DELAY_MS,
              (ticket.expiresIn - RENEW_MARGIN_SECONDS) * 1000,
            );
            timer = window.setTimeout(load, delay);
          }
        })
        .catch((err: unknown) => {
          if (cancelled || controller.signal.aborted) return;
          tokenRef.current = null;
          setStatus('error');
          setError(err instanceof Error ? err.message : 'Acces refuzat.');
          setErrorStatus(err instanceof TicketError ? err.status : 0);
          timer = window.setTimeout(load, RETRY_DELAY_MS);
        });
    };

    load();

    return () => {
      cancelled = true;
      controller.abort();
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [demo, renewNonce]);

  const getToken = useCallback(() => tokenRef.current, []);

  const renew = useCallback(() => {
    if (demo) return;
    // Trecerea prin „loading” e feedbackul apăsării: fără ea, butonul de
    // reîncercare nu arată nimic până la următorul răspuns.
    setStatus('loading');
    setRenewNonce((nonce) => nonce + 1);
  }, [demo]);

  const value = useMemo<SessionContextValue>(
    () => ({ status, error, errorStatus, getToken, tokenVersion, renew }),
    [status, error, errorStatus, getToken, tokenVersion, renew],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}

export function useSession(): SessionContextValue {
  const ctx = useContext(SessionContext);
  if (!ctx) throw new Error('useSession trebuie folosit în interiorul <SessionProvider>');
  return ctx;
}
